import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import useUnreadByCategory from '../hooks/useUnreadByCategory';
import CourseAnnouncementsBoard from '../components/CourseAnnouncementsBoard';
import PendingTasksCard from '../components/PendingTasksCard';
import {
  Textbox, MicrophoneStage, Headphones, BookOpen, PencilLine,
  GameController, Lightning, FilmStrip,
  FileText, Video, UsersThree, Question, ChartLineUp,
} from '@phosphor-icons/react';

// Üben dropdown quick-access ——————————————————————————
const UBEN = [
  { to: '/vocabulary',                        key: 'vocabulary',  label: { de: 'Vokabeln',            es: 'Vocabulario' },            icon: Textbox,        color: '#5B8C9A' },
  { to: '/materials?tile=speaking',           key: 'speaking',    label: { de: 'Sprechen',            es: 'Hablar' },                 icon: MicrophoneStage,color: '#AB1C24' },
  { to: '/materials?tile=listening',          key: 'listening',   label: { de: 'Hören',               es: 'Escuchar' },               icon: Headphones,    color: '#D97706' },
  { to: '/schreiben',                         key: 'writing',     label: { de: 'Schreiben',           es: 'Escribir' },               icon: PencilLine,     color: '#8B5A3C' },
  { to: '/materials?tile=reading_exercises',  key: 'reading',     label: { de: 'Lesen',               es: 'Leer' },                   icon: BookOpen,       color: '#2F6B50' },
  { to: '/ai-teacher',                        key: 'ai_teacher',  label: { de: 'Spiele',              es: 'Juegos' },                 icon: GameController, color: '#6D28D9' },
  { to: '/challenges',                        key: 'challenges',  label: { de: 'Challenge',           es: 'Desafíos' },               icon: Lightning,      color: '#E06D53' },
  { to: '/videos',                            key: 'videos',      label: { de: 'Media',               es: 'Media' },                  icon: FilmStrip,      color: '#AB1C24' },
];

// Kurs dropdown quick-access ——————————————————————————
// Order matches the user-facing spec: Videoanruf · Grammatik · Buch-Übungen ·
// Analyse · Fragen. Freunde was moved out of Kurs (now lives at the top of
// the Challenge page). Vokabeln, Plattform and the duplicate inner-Kurs tile
// were removed because they either lived in Üben already (Vokabeln) or were
// pure redundant navigation (Plattform / Kurs).
const KURS = [
  { to: '/classes',                        key: 'classes',   label: { de: 'Videoanruf',     es: 'Videollamada' }, icon: Video,       color: '#AB1C24' },
  { to: '/materials?tile=grammar',         key: 'grammar',   label: { de: 'Grammatik',      es: 'Gramática' },    icon: BookOpen,    color: '#8B5A3C' },
  { to: '/materials?tile=exercises',       key: 'exercises', label: { de: 'Buch-Übungen',   es: 'Ejercicios libro' }, icon: FileText, color: '#AB1C24' },
  { to: '/lernanalyse',                    key: 'analysis',  label: { de: 'Analyse',        es: 'Análisis' },     icon: ChartLineUp, color: '#0EA5E9' },
  { to: '/dudas',                          key: 'dudas',     label: { de: 'Fragen',         es: 'Dudas' },        icon: Question,    color: '#E06D53' },
];

/**
 * Two flavours of the quick-access tile:
 *   - compact (Home): small horizontal tile so both UBEN + KURS fit on screen
 *     without scrolling on desktop.
 *   - large (/uben, /kurs sub-pages): roomier tile with bigger icon and label.
 */
function QuickTile({ t, lang, badge, testId, compact }) {
  const Icon = t.icon;

  if (compact) {
    return (
      <Link
        to={t.to}
        data-testid={testId}
        className="group relative bg-white rounded-xl border border-stone-200 hover:shadow-md hover:-translate-y-0.5 transition-all p-3 flex items-center gap-3 min-h-[64px]"
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform"
          style={{ backgroundColor: `${t.color}15` }}
        >
          <Icon size={22} weight="duotone" style={{ color: t.color }} />
        </div>
        <span className="flex-1 min-w-0 text-sm md:text-base font-semibold text-[#292524] leading-tight break-words hyphens-auto">
          {t.label[lang] || t.label.de}
        </span>
        {badge > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#AB1C24] text-white text-[11px] font-bold shadow ring-2 ring-white flex-shrink-0"
            data-testid={`${testId}-badge`}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </Link>
    );
  }

  return (
    <Link
      to={t.to}
      data-testid={testId}
      className="group relative bg-white rounded-xl md:rounded-2xl border border-stone-200 hover:shadow-md hover:-translate-y-0.5 transition-all p-3 md:p-6 flex items-center gap-3 md:gap-5 min-h-[64px] md:min-h-[140px] [&_svg.tile-icon]:md:!w-9 [&_svg.tile-icon]:md:!h-9"
    >
      <div
        className="w-10 h-10 md:w-16 md:h-16 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform"
        style={{ backgroundColor: `${t.color}15` }}
      >
        <Icon size={22} weight="duotone" className="tile-icon" style={{ color: t.color }} />
      </div>
      <span className="flex-1 min-w-0 text-sm md:text-xl font-semibold text-[#292524] leading-tight break-words hyphens-auto">
        {t.label[lang] || t.label.de}
      </span>
      {badge > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[22px] h-[22px] md:min-w-[26px] md:h-[26px] px-1.5 rounded-full bg-[#AB1C24] text-white text-[11px] md:text-xs font-bold shadow ring-2 ring-white flex-shrink-0 absolute md:static top-2 right-2"
          data-testid={`${testId}-badge`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

export default function Dashboard({ only } = {}) {
  const { user } = useAuth();
  const { lang } = useLang();
  const unread = useUnreadByCategory();
  const isDe = lang === 'de';
  const name = user?.name?.split(' ')[0] || (user?.email || '').split('@')[0];

  const isHome = !only;
  const showUben = isHome || only === 'uben';
  const showKurs = isHome || only === 'kurs';
  const subtitle = isHome
    ? (isDe ? 'Schnellzugriff — wähle einen Bereich.' : 'Acceso rápido — elige un apartado.')
    : only === 'uben'
      ? (isDe ? 'Wähle eine Übung.' : 'Elige una actividad.')
      : (isDe ? 'Wähle einen Kursbereich.' : 'Elige un apartado del curso.');

  // Grid classes differ between Home (dense 4-col on desktop so both
  // sections fit without scrolling) and sub-pages (3-col, larger tiles).
  const gridClass = isHome
    ? 'grid grid-cols-2 md:grid-cols-4 gap-3'
    : 'grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5';

  return (
    <div className={isHome ? 'space-y-5' : 'space-y-8'} data-testid="home-dashboard">
      <header>
        <h1 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-[#292524] leading-tight">
          {isDe ? 'Hallo' : 'Hola'}, <span className="text-[#AB1C24]">{name}</span>
        </h1>
        <p className="text-sm sm:text-base text-[#57534E] mt-1">{subtitle}</p>
      </header>

      {showUben && (
        <section data-testid="home-section-uben">
          {isHome && (
            <h2 className="font-heading text-lg sm:text-xl font-bold text-[#292524] mb-3">
              {isDe ? 'Üben' : 'Practicar'}
            </h2>
          )}
          <div className={gridClass}>
            {UBEN.map(t => (
              <QuickTile key={t.key} t={t} lang={lang} badge={unread[t.key] || 0} testId={`home-tile-${t.key}`} compact={isHome} />
            ))}
          </div>
          <PendingTasksCard isDe={isDe} />
        </section>
      )}

      {showKurs && (
        <section data-testid="home-section-kurs">
          {isHome && (
            <h2 className="font-heading text-lg sm:text-xl font-bold text-[#292524] mb-3">
              {isDe ? 'Kurs' : 'Curso'}
            </h2>
          )}
          <div className={gridClass}>
            {KURS.map(t => (
              <QuickTile key={t.key} t={t} lang={lang} badge={unread[t.key] || 0} testId={`home-tile-${t.key}`} compact={isHome} />
            ))}
          </div>
          <PendingTasksCard isDe={isDe} />
          {/* The "tablón" — teacher posts and auto-emitted "Neues Material …"
              notices appear directly below the Kurs tiles. */}
          <CourseAnnouncementsBoard />
        </section>
      )}
    </div>
  );
}
